/**
 * ZONE DIALOG — Crea / modifica una zona: nome, colore, commerciale. Le
 * province si gestiscono a parte (ZoneRegionDialog).
 *
 * In modifica il PATCH invia solo i campi cambiati; togliere il commerciale
 * manda `liferayUserId: 0`, che per il backend vuol dire «nessuno».
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { zoneApi } from '../../entities/zone/zoneApi';
import { extractError } from '../../store/thunkHelpers';
import type { Zone, ZoneUpdatePayload, ZoneUser } from '../../entities/zone/zoneTypes';
import { useToast } from '../useToast';
import { ColorPicker } from '../ColorPicker';

const HEX6 = /^#[0-9a-f]{6}$/i;
const DEFAULT_COLOR = '#2563eb';

interface Props {
  groupId: number;
  /** Zona in modifica; assente = nuova. */
  zone?: Zone;
  /** Zone esistenti: i loro nomi sono già usati. */
  zones: Zone[];
  onClose: () => void;
  onSaved: () => void;
}

export function ZoneDialog({ groupId, zone, zones, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [users, setUsers] = useState<ZoneUser[]>([]);
  const [name, setName] = useState(zone?.name ?? '');
  const [color, setColor] = useState(zone?.color ?? DEFAULT_COLOR);
  // In modifica il commerciale attuale è un'opzione a sé: può non essere più fra gli utenti attivi.
  const current: ZoneUser | null = zone && zone.liferayUserId > 0
    ? { userId: zone.liferayUserId, fullName: zone.liferayUserName || `#${zone.liferayUserId}` }
    : null;
  const [user, setUser] = useState<ZoneUser | null>(current);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    zoneApi.users()
      .then(setUsers)
      .catch((err) => {
        toast({ severity: 'error', message: extractError(err) });
        onClose();
      });
    // Si carica all'apertura: `toast` e `onClose` non fanno parte della chiave.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Come il backend: stesso nome a meno di maiuscole e spazi.
  const trimmed = name.trim();
  const duplicate = zones.some((z) => z.zoneId !== zone?.zoneId && z.name.trim().toLowerCase() === trimmed.toLowerCase());
  const userOptions = current && !users.some((u) => u.userId === current.userId) ? [current, ...users] : users;
  const validColor = HEX6.test(color);
  const userId = user?.userId ?? 0;
  // In modifica, senza campi cambiati non c'è nulla da inviare (niente PATCH vuoto).
  const changed = !zone || trimmed !== zone.name || color.toLowerCase() !== zone.color || userId !== zone.liferayUserId;
  const canSave = trimmed !== '' && !duplicate && validColor && changed && !saving;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    const normalized = color.toLowerCase();
    try {
      if (zone) {
        const patch: ZoneUpdatePayload = {};
        if (trimmed !== zone.name) patch.name = trimmed;
        if (normalized !== zone.color) patch.color = normalized;
        if (userId !== zone.liferayUserId) patch.liferayUserId = userId;
        await zoneApi.update(zone.zoneId, patch);
      } else {
        await zoneApi.create(groupId, { name: trimmed, color: normalized, liferayUserId: userId });
      }
      onSaved();
    } catch (err) {
      toast({ severity: 'error', message: extractError(err) });
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="tc-head">
        <span className="tc-kicker">{t(zone ? 'settings.zones.editZone' : 'settings.zones.newZone')}</span>
        <IconButton aria-label={t('common.cancel')} onClick={onClose} size="small" className="tc-x">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent className="app-dialog__body">
          <TextField
            label={t('settings.zones.fields.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
            required
            autoFocus
            error={duplicate}
            helperText={duplicate ? t('settings.zones.nameUsed') : undefined}
            className="form-field"
            slotProps={{ htmlInput: { maxLength: 75 } }}
          />

          <div className="zone-settings__color">
            <ColorPicker
              label={t('settings.zones.fields.color')}
              value={validColor ? color.toLowerCase() : DEFAULT_COLOR}
              onChange={setColor}
            />
            <TextField
              label={t('settings.zones.fields.color')}
              value={color}
              onChange={(e) => setColor(e.target.value)}
              size="small"
              fullWidth
              required
              error={!validColor}
              className="form-field"
              slotProps={{ htmlInput: { maxLength: 7 } }}
            />
          </div>

          <Autocomplete
            options={userOptions}
            value={user}
            onChange={(_, v) => setUser(v)}
            getOptionLabel={(o) => o.fullName}
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            noOptionsText={t('settings.zones.noUsers')}
            renderInput={(params) => (
              <TextField {...params} label={t('settings.zones.fields.salesperson')} size="small" className="form-field" />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button type="button" variant="outlined" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={!canSave}>{t('common.save')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
